import { get } from "lodash";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Formik, Form } from "formik";
import FormikEffect from "./formik/FormikEffect";

import * as Yup from "yup";
import * as Schemas from "./validationSchemas";

import FormikDetailsPanel from "./formik/FormikDetailsPanel/FormikDetailsPanel.jsx";
import FormikActionsPanel from "./formik/FormikActionsPanel.jsx";
import EditOTPBlock from "./EditOTPBlock";
import EditSizeBlock from "./EditSizeBlock";

import { OPERATIONS } from "../../constants/operations.js";

import { selectOperationAvailability } from "../../state/reducers/operations/operationSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class EditDetailsForm extends PureComponent {
  static propTypes = {
    data: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onFormikSubmit: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    copyKey: PropTypes.string.isRequired,

    violationErrors: PropTypes.object,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    disabled: false,
    copyKey: "resource.details_panel"
  };

  constructor(props) {
    super(props);

    this.state = {
      initialValues: EditDetailsForm.createInitialValuesObject(props)
    };
  }

  static getDerivedStateFromProps = (props, state) => {
    const newInitialValues = EditDetailsForm.createInitialValuesObject(props);

    if (
      newInitialValues.name !== state.initialValues.name ||
      newInitialValues.description !== state.initialValues.description ||
      newInitialValues.size !== state.initialValues.size ||
      newInitialValues.requiresUserVerification !==
        state.initialValues.requiresUserVerification
    ) {
      return newInitialValues;
    }

    return null;
  };

  static createInitialValuesObject(props) {
    return {
      name: get(props, "data.name", ""),
      description: get(props, "data.description", ""),
      size: get(props, "data.size.@id", undefined),
      requiresUserVerification: Number(
        get(props, "data.requiresUserVerification", 0)
      ),
      ignoreChangeInSideEffect: get(
        props,
        "data.ignoreChangeInSideEffect",
        false
      )
    };
  }

  onChange = (prevFormikProps, formikProps) => {
    const { data, onChange } = this.props;

    // we only want to make onChange calls when the size changes
    if (
      formikProps.values.size === data.size["@id"] ||
      formikProps.values.size === prevFormikProps.values.size
    ) {
      return null;
    }

    if (onChange) onChange(prevFormikProps, formikProps);
  };

  // -- VALIDATION -- //

  validationScheme = () => {
    const messages = this.props.copy("forms.validation_requirements");

    return Yup.object().shape({
      name: Schemas.simpleStringSchema("Name", messages, true),
      description: Yup.string(),
      size: Schemas.requiredWhenDefinedStringSchema("Size", messages)
    });
  };

  render() {
    const {
      data,
      onCancel,
      onReset,
      onFormikSubmit,
      violationErrors,
      resizeEntityAvailability,
      editAvailability,
      copyKey,
      copy
    } = this.props;

    if (!data) return null;

    const otpAvailable = data.operations
      ?.find(op => op.name === OPERATIONS.UPDATE)
      ?.fields?.some(field => field.name === "requiresUserVerification");

    return (
      <Formik
        initialValues={this.state.initialValues} // required by semantic-ui react
        validateOnMount={true}
        enableReinitialize={false}
        validationSchema={this.validationScheme(resizeEntityAvailability)}
        onSubmit={onFormikSubmit}
      >
        {formikProps => {
          return (
            <Fragment>
              <Form className="ui form">
                {data.size && <FormikEffect onChange={this.onChange} />}

                <FormikDetailsPanel
                  readOnly={false}
                  apiErrors={violationErrors}
                />

                {Object.hasOwn(data, "requiresUserVerification") &&
                  otpAvailable && (
                    <EditOTPBlock
                      operations={data.operations}
                      formikProps={formikProps}
                      editAvailability={editAvailability}
                    />
                  )}

                {data.size && (
                  <EditSizeBlock
                    formikProps={formikProps}
                    data={data}
                    resizeAvailability={resizeEntityAvailability}
                  />
                )}

                <FormikActionsPanel
                  onCancel={onCancel}
                  reset={true}
                  onReset={onReset}
                  submit={true}
                  useSubmitModal={true}
                  modalCopy={copy(`${copyKey}.modals`)}
                  panelCopy={copy(`${copyKey}.actions_panel`)}
                />
              </Form>
            </Fragment>
          );
        }}
      </Formik>
    );
  }
}

const mapStateToProps = (state, props) => ({
  resizeEntityAvailability: selectOperationAvailability(
    state,
    props,
    OPERATIONS.RESIZE
  ),
  editAvailability: selectOperationAvailability(
    state,
    props,
    OPERATIONS.UPDATE
  ),
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(EditDetailsForm);
